<template>
  <Card>
    <CardHeading
      :heading="heading"
      :showCapacity="!editingResources"
      :currentItems="resources.length"
      :maxItems="6"
      :showButton="!editingResources"
      buttonText="Add resource"
      @button-click="handleNewResources"
    />
    <Callout v-if="resources.length === 6" message="Resources limit reached" />
    <div v-if="!editingResources && resources[0]">
      <draggable
        v-model="resources"
        @end="reorder($event, 'resources')"
        v-bind="dragOptions"
        :options="{ handle: '.handle-wrapper' }"
      >
        <transition-group>
          <ProfileContentItem
            v-for="(resource, i) in resources"
            :key="i + 1"
            :title="resource.name"
            @edit-click="handleResourcesEditClick(resource)"
            @delete-click="
              $emit('delete-content', {
                item: resource,
                section: 'resources',
                modalTitle: 'Delete resource?',
                modalDescription:
                  'This action cannot be undone, are you sure you want to delete this resource?',
              })
            "
          />
        </transition-group>
      </draggable>
    </div>
    <CardText
      v-if="!editingResources && !resources[0]"
      text="No links have been added."
    />
    <CardFormWrapper v-if="editingResources">
      <Input
        :value="editingResources.data.name"
        @input-change="
          handleEdit({ section: 'editingResources', data: $event })
        "
        name="name"
        label="Resource name"
        styles="font-weight: 400"
        placeholder="Resource name"
      />

      <label
        style="margin: 1rem 0 0.5rem; font-size: 0.875rem; font-weight: 400"
        >Image or PDF</label
      >

      <div style="display: flex">
        <input
          type="radio"
          id="resource-link"
          name="resource-type"
          value="link"
          v-model="resourcesRadio"
        />
        <label
          class="fs-sm"
          style="margin-left: 10px; font-weight: 400; margin-bottom: 0"
          for="resource-link"
          >{{ getlabel() }}</label
        >
      </div>
      <div style="margin-left: 1.75rem">
        <Input
          v-if="resourcesRadio === 'link'"
          :value="editingResources.data[getResourceTypeAction()]"
          @input-change="
            handleEdit({ section: 'editingResources', data: $event })
          "
          @error-change="updateError({ state: $event, key: 'resourceLink' })"
          :name="getResourceTypeAction()"
          :fieldValidator="validateWebsite"
          placeholder="https://example.com/resource"
        />
      </div>

      <div style="display: flex; margin-top: 1rem">
        <input
          type="radio"
          id="resource-upload"
          name="resource-type"
          value="file"
          v-model="resourcesRadio"
        />
        <label
          class="fs-sm"
          style="margin-left: 10px; font-weight: 400"
          for="resource-upload"
          >Upload an image or PDF</label
        >
      </div>
      <div style="margin-left: 1.75rem">
        <FileUploader
          v-if="resourcesRadio === 'file'"
          accept=".png, .jpg, .jpeg, .gif, .pdf"
          :existingImg="editingResources.data.file || undefined"
          @update-image="
            handleFileChange($event, handleEdit, 'editingResources')
          "
          buttonText="Remove file"
          field="file"
          zoneText="Click or drag here to upload your file."
          uploadDescription="<p class='fs-sm' style='color: #727a88;'><span style='font-weight: 600;'>File type:</span> JPG, PNG, or PDF | <span style='font-weight: 600'>Min-width:</span> 400px | <span style='font-weight: 600'>Background:</span> Transparent or white</p>"
        />
      </div>

      <div class="flex" style="justify-content: flex-end; margin-top: 2rem">
        <Button @button-click="handleResourcesCancelClick" bType="secondary">
          Cancel
        </Button>
        <Button
          :bMargin="servicePartnerProfile ? 'left' : ''"
          :bDisabled="!resourceFormComplete"
          @button-click="handleResourcesDoneClick"
        >
          Done
        </Button>
      </div>
    </CardFormWrapper>
  </Card>
</template>

<script>
import Callout from '@/components/DesignSystem/Callout';
import Button from '@/components/DesignSystem/Button';
import Card from '@/components/DesignSystem/Card';
import Input from '@/components/DesignSystem/Input';
import CardHeading from '@/components/DesignSystem/CardHeading';
import FileUploader from '@/components/DesignSystem/FileUploader';
import CardText from '@/components/DesignSystem/CardText';
import CardFormWrapper from '@/components/DesignSystem/CardFormWrapper';
import ProfileContentItem from '@/components/Shared/ProfileContentItem';
import draggable from 'vuedraggable';
import { handleFileChange } from '@/utils/files';
import Vue from 'vue';
import { validateWebsite } from '@/components/DesignSystem/validators';

export default {
  components: {
    Button,
    draggable,
    Card,
    Input,
    Callout,
    CardHeading,
    FileUploader,
    CardText,
    CardFormWrapper,
    ProfileContentItem,
  },
  props: {
    namespace: {
      type: String,
    },
    heading: {
      type: String,
      default: 'Resources',
    },
    servicePartnerProfile: Boolean,
    agencyOPP: Boolean,
    storeInfo: Object,
  },
  data() {
    return {
      editingResources: false,
      resourcesRadio: 'link',
    };
  },
  computed: {
    resourceFormComplete() {
      if (this.editingResources && this.editingResources.data) {
        if (
          this.editingResources.data.name &&
          (this.editingResources.data.file ||
            this.editingResources.data[this.getResourceTypeAction()])
        ) {
          if (this.resourcesRadio === 'link') {
            return !this.resourceLinkError;
          } else {
            return true;
          }
        }
      }
      return false;
    },
    resourceLinkError() {
      return this.$store.getters[`${this.namespace}/resourceLinkError`];
    },
    resources: {
      get() {
        return this.$store.getters[
          `${this.namespace}/${
            this.storeInfo ? this.storeInfo.get : 'resources'
          }`
        ].filter(i => !i.delete);
      },
      set(value) {
        this.$store.commit(
          `${this.namespace}/${
            this.storeInfo ? this.storeInfo.set : 'updateResources'
          }`,
          value
        );
      },
    },
    dragOptions() {
      return {
        animation: 200,
        ghostClass: 'ghost',
      };
    },
  },
  methods: {
    validateWebsite,
    handleEdit(payload) {
      Vue.set(
        this[payload.section]['data'],
        payload.data.key,
        payload.data.value
      );
      if (payload.uri) {
        Vue.set(this[payload.section]['data'], 'uri', payload.uri);
      }
      this.$forceUpdate();
    },
    handleDoneClick({ item, section }) {
      if (this[item]['new']) {
        this.addItemForResources({ section, data: this[item]['data'] });
      } else {
        this.editItemForResources({ section, data: this[item]['data'] });
      }
      return (this[item] = false);
    },
    addItemForResources(payload) {
      this.$store.dispatch(
        `${this.namespace}/${
          this.storeInfo ? this.storeInfo.add : 'addItemForResources'
        }`,
        payload
      );
    },
    editItemForResources(payload) {
      this.$store.dispatch(
        `${this.namespace}/${
          this.storeInfo ? this.storeInfo.edit : 'editItemForResources'
        }`,
        payload
      );
    },
    updateError(payload) {
      this.$store.dispatch(`${this.namespace}/updateError`, payload);
    },
    handleResourcesCancelClick() {
      this.resourcesRadio = 'link';
      this.editingResources = false;
    },
    handleResourcesDoneClick() {
      this.resourcesRadio = 'link';
      this.handleDoneClick({
        item: 'editingResources',
        section: 'resources',
      });
    },
    handleResourcesEditClick(resource) {
      this.editingResources = {
        new: false,
        data: { ...resource, update: true },
      };
      if (resource[this.getResourceTypeAction()]) {
        return (this.resourcesRadio = 'link');
      }
      return (this.resourcesRadio = 'file');
    },

    handleFileChange,

    reorder(event, section) {
      this[section] = this[section].map((item, i) => {
        return {
          ...item,
          order: i,
        };
      });
      this.findItemsToUpdate(event, section);
      this.$store.dispatch(
        `${this.namespace}/${
          this.storeInfo ? this.storeInfo.dirty : 'addDirtyTab'
        }`,
        {
          storeKey: section,
        }
      );
    },
    findItemsToUpdate(event, section) {
      const start = event.oldIndex;
      const end = event.newIndex;

      if (start < end) {
        for (let i = start; i <= end; i++) {
          if (this[section][i]['id']) {
            this.$set(this[section][i], 'update', true);
          }
        }
      } else {
        for (let i = end; i <= start; i++) {
          if (this[section][i]['id']) {
            this.$set(this[section][i], 'update', true);
          }
        }
      }
    },
    handleNewResources() {
      this.editingResources = {
        new: true,
        data: {
          order: this.resources.length,
          name: '',
          file: '',
        },
      };
      this.$set(this.editingResources.data, this.getResourceTypeAction(), '');
    },
    getlabel() {
      const isNonUrlFile = this.servicePartnerProfile || this.agencyOPP;
      return `Link to ${
        isNonUrlFile ? 'an image or PDF' : 'a file with a URL'
      }`;
    },
    getResourceTypeAction() {
      if (this.servicePartnerProfile || this.agencyOPP) {
        return 'resourceUrl';
      }
      return 'url';
    },
  },
};
</script>

<style lang="scss" scoped>
.disabled {
  cursor: not-allowed !important;
}

.fa-angle-left {
  margin-right: 5px;
}

.preview-button {
  margin-top: 1rem;
  color: #4d48e8;
  font-weight: 600;
  font-size: 0.875rem;
  display: flex;
  align-items: center;

  svg {
    margin-right: 5px;
    fill: #4d48e8;
  }
}

.horizontal-line {
  color: #dadee6;
  margin: 2rem 0;
}

.ghost {
  opacity: 0;
}

.button-type-primary {
  margin-left: 8px;
}
</style>
